import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { type ChartConfig, ChartContainer } from '@/components/ui/chart';
import type { ScoreTrendPoint, TrendBucket } from '@/pages/analytics/types';
import { AXIS_LINE_STYLE, TICK_LINE_STYLE } from '@/pages/analytics/utils/chartAxis';
import { formatChartAxisDate, formatChartTooltipDate } from '@/pages/analytics/utils/chartDate';
import {
  COLOR_POSITIVE,
  COLOR_PRIMARY,
  colorForSeriesIndex,
} from '@/pages/analytics/utils/chartPalette';
import { pivotSeries, scopeRows } from '@/pages/analytics/utils/series';

import { ChartEmptyState } from './ChartStates';
import { DotsLegend } from './DotsLegend';
import type { ChartScope } from './ScopeToggle';

interface ScoreTrendChartProps {
  data: ScoreTrendPoint[];
  scope: ChartScope;
  trendBucket?: TrendBucket;
  /** Height in pixels. */
  height?: number;
}

const formatPercentTick = (v: number) => `${Math.round(v * 100)}%`;
const formatTooltipPercent = (v: unknown) =>
  typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : '';

// Bottom-of-chart legend for the score-band threshold lines. Rendered
// in `overall` scope where a series legend would be redundant. The dashed
// swatches mirror the strokeDasharray on the ReferenceLines themselves.
// Below the Meets line (95%) is the Unsatisfactory band.
const SCORE_THRESHOLDS = [
  { label: 'Exemplary (98+)', color: COLOR_POSITIVE },
  { label: 'Meets (95+)', color: COLOR_PRIMARY },
];

const ScoreThresholdsLegend: React.FC = () => (
  <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 pt-3 text-[11px]">
    {SCORE_THRESHOLDS.map(({ label, color }) => (
      <li key={label} className="inline-flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block w-6 border-t-2 border-dashed shrink-0"
          style={{ borderColor: color }}
        />
        <span className="text-foreground">{label}</span>
      </li>
    ))}
  </ul>
);

export const ScoreTrendChart: React.FC<ScoreTrendChartProps> = ({
  data,
  scope,
  trendBucket = 'day',
  height = 280,
}) => {
  const { data: rows, series } = useMemo(
    () =>
      pivotSeries(
        scopeRows(data, scope),
        p => p.date,
        p => p.avg_score,
        'omit',
      ),
    [data, scope],
  );
  const colorBySeriesKey = useMemo(
    () =>
      new Map(
        series.map((s, index) => [
          s.key,
          s.key === 'overall' ? COLOR_PRIMARY : colorForSeriesIndex(index),
        ]),
      ),
    [series],
  );

  const config: ChartConfig = useMemo(() => {
    const c: ChartConfig = {};
    series.forEach(s => {
      c[s.key] = { label: s.name, color: colorBySeriesKey.get(s.key) ?? COLOR_PRIMARY };
    });
    return c;
  }, [colorBySeriesKey, series]);

  // Compute a y-domain that hugs the data. Healthy orgs usually trend in
  // 0.85–1.0 so we want the chart zoomed in there, but agent detail and
  // struggling cohorts can dip much lower — a hardcoded floor at 0.6 was
  // clipping those. Floor at the smaller of (min - 5pp, 0.6) so high
  // performers stay zoomed but low-performing series still fit.
  const yMin = useMemo(() => {
    let min = Infinity;
    for (const row of rows) {
      for (const [k, v] of Object.entries(row)) {
        if (k === 'date') continue;
        const n = Number(v);
        if (Number.isFinite(n) && n < min) min = n;
      }
    }
    if (!Number.isFinite(min)) return 0.6;
    const padded = Math.floor((min - 0.05) * 100) / 100;
    return Math.max(0, Math.min(0.6, padded));
  }, [rows]);

  if (!rows.length) {
    return <ChartEmptyState height={height} />;
  }

  const ariaLabel =
    scope === 'overall'
      ? `Score trend line chart, overall average quality score across ${rows.length} points`
      : `Score trend line chart, ${series.length} program${series.length === 1 ? '' : 's'} across ${rows.length} points`;

  return (
    <ChartContainer
      config={config}
      className="!aspect-auto w-full"
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <LineChart data={rows} margin={{ top: 10, right: 18, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={value => formatChartAxisDate(value, trendBucket)}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
        />
        <YAxis
          domain={[yMin, 1]}
          tickFormatter={formatPercentTick}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
          width={36}
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
          // Lift the tooltip above adjacent chart cards; without a z-index the
          // wrapper paints under the sibling chart in the 2-col grid (AQ-476).
          wrapperStyle={{ zIndex: 20 }}
          formatter={formatTooltipPercent}
          labelFormatter={value => formatChartTooltipDate(value, trendBucket)}
        />

        <ReferenceLine
          y={0.98}
          stroke={COLOR_POSITIVE}
          strokeDasharray="4 4"
          label={{ value: '98', fontSize: 10, fill: COLOR_POSITIVE, position: 'right' }}
        />
        <ReferenceLine
          y={0.95}
          stroke={COLOR_PRIMARY}
          strokeDasharray="4 4"
          label={{ value: '95', fontSize: 10, fill: COLOR_PRIMARY, position: 'right' }}
        />

        {series.map(s => (
          <Line
            key={s.key}
            name={s.name}
            type="monotone"
            dataKey={s.key}
            stroke={colorBySeriesKey.get(s.key) ?? COLOR_PRIMARY}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5 }}
            animationDuration={500}
            connectNulls
          />
        ))}

        {/* Program-split view: legend names the program series (or the
            single program in scope). */}
        {scope === 'by_program' && series.length > 0 && (
          <Legend
            content={<DotsLegend />}
            verticalAlign="bottom"
            // Push the legend just below the plot area; recharts' default is
            // a fixed 30px-tall slot at the bottom which the LineChart already
            // reserves once we render the Legend element.
            wrapperStyle={{ paddingTop: 4 }}
          />
        )}

        {/* Overall view: legend identifies the threshold reference lines
            (Exemplary / Meets). Series legend would be redundant here since
            there's only the single trend line. */}
        {scope === 'overall' && (
          <Legend
            content={<ScoreThresholdsLegend />}
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 4 }}
          />
        )}
      </LineChart>
    </ChartContainer>
  );
};
